import { AuthContext } from '@/auth/context';
import { ImpersonationAlertBanner } from '@/auth/impersonation-alert-banner';
import { GeneralErrorBoundary } from '@/components/general-error-boundary';
import { ImpersonationSwitcher } from '@/components/impersonation-switcher';
import { LoadingPage } from '@/components/loading-page';
import { Button } from '@/components/ui/button';
import { EmptyArray } from '@/lib/api-calls';
import { DEFAULT_PAGE_SIZE } from '@/lib/pagination';
import { ExternalNavLink, NavLink, NavLinkDefinition } from '@/routes/_layout/-components/nav-link';
import { UserAvatarMenu } from '@/routes/_layout/-components/user-avatar-menu';
import { NAV } from '@airreading/automation-test/test-ids';
import { isAirAdmin, isCoach, isCoachAllStatuses, isEdgeCoach, isStudent } from '@airreading/common';
import { LoggedInUser, maxBookPartForStudentGrade, StudentYearSelectable } from '@airreading/common/data';
import { createFileRoute, Link, Navigate, Outlet, useMatchRoute } from '@tanstack/react-router';
import {
  Book,
  ClipboardCheck,
  HeartHandshake,
  CircleUser,
  Home,
  Library,
  CalendarCheck,
  PersonStanding,
  GraduationCap,
  BookOpen,
} from 'lucide-react';
import { useContext } from 'react';
import CalendarIcon from './assets/calendar-icon.svg?react';
import CalendarDollarIcon from './assets/calendar-dollar-icon.svg?react';
import SchoolsIcon from './assets/schools-icon.svg?react';
import SessionsIcon from './assets/sessions-icon.svg?react';
import StudentsIcon from './assets/students-icon.svg?react';
import TeachersIcon from './assets/teachers-icon.svg?react';
import BellIcon from './assets/bell-icon.svg?react';
import { useErrorLogger } from '@/hooks/use-error-logger';
import { cn } from '@/lib/tailwind-utils';
import { shouldRenderItem } from '@/components/should-render-item';
import { useFeatureFlag } from '@/hooks/use-feature-flags';

export const Route = createFileRoute('/_layout')({
  component: RootLayoutComponent,
});

const getNavLinks = (pm2Enabled: boolean): NavLinkDefinition[] => [
  {
    label: 'Air Reading Class',
    iconComponent: Home,
    linkProps: { to: '/' },
    shouldRender: isStudent,
  },
  {
    label: 'My classes',
    iconComponent: CalendarIcon,
    linkProps: { to: '/my-classes' },
    shouldRender: isCoach,
    children: [
      {
        label: 'Upcoming classes',
        iconComponent: CalendarIcon,
        linkProps: { to: '/my-classes/upcoming', search: { pageSize: 30, pageIndex: 0 } },
        shouldRender: isCoach,
      },
      {
        label: 'Past classes',
        iconComponent: CalendarIcon,
        linkProps: { to: '/my-classes/past', search: { pageSize: 30, pageIndex: 0 } },
        shouldRender: isCoach,
      },
    ],
  },
  {
    label: 'Class feedback',
    iconComponent: ClipboardCheck,
    linkProps: { to: '/teachers-feedback' },
    shouldRender: isCoach,
  },
  { isDivider: true, shouldRender: isCoach },
  {
    label: 'My schedule',
    iconComponent: CalendarCheck,
    linkProps: { to: '/my-schedule' },
    shouldRender: (user) => isCoach(user) && !isEdgeCoach(user),
  },
  {
    label: 'Account',
    iconComponent: CircleUser,
    linkProps: { to: '/account' },
    shouldRender: isStudent,
  },
  {
    label: 'Academy',
    iconComponent: GraduationCap,
    externalURL: 'https://help.airreading.com/support/solutions/157000561205',
    shouldRender: isCoach,
  },
  {
    label: 'Payments',
    iconComponent: CalendarDollarIcon,
    externalURL: 'https://app.airreading.com/pay/current',
    shouldRender: (user) => isCoach(user) && !isEdgeCoach(user) && !pm2Enabled,
  },
  {
    label: 'Payments',
    iconComponent: CalendarDollarIcon,
    linkProps: { to: '/payments' },
    shouldRender: (user) => isCoach(user) && !isEdgeCoach(user) && pm2Enabled,
  },
  {
    label: 'Help center',
    iconComponent: BellIcon,
    externalURL: 'https://help.airreading.com/support/home',
    shouldRender: isCoach,
  },
  {
    label: 'Request substitutes',
    iconComponent: HeartHandshake,
    externalURL: 'https://help.airreading.com/support/tickets/new?ticket_form=request_a_substitute',
    shouldRender: (user) => isCoach(user) && !isEdgeCoach(user),
  },
  { isDivider: true, shouldRender: isCoach },
  {
    label: 'Account',
    iconComponent: CircleUser,
    linkProps: { to: '/profile' },
    shouldRender: isCoachAllStatuses,
  },
  { isDivider: true, shouldRender: isCoach },
  {
    label: 'Sessions',
    iconComponent: SessionsIcon,
    linkProps: { to: '/sessions' },
    shouldRender: isAirAdmin,
  },
  {
    label: 'Schools',
    iconComponent: SchoolsIcon,
    linkProps: { to: '/orgs/schools', search: { pageSize: DEFAULT_PAGE_SIZE, pageIndex: 0 } },
    shouldRender: isAirAdmin,
  },
  {
    label: 'Teachers',
    iconComponent: TeachersIcon,
    linkProps: { to: '/teachers' },
    shouldRender: isAirAdmin,
  },
  {
    label: 'Students',
    iconComponent: StudentsIcon,
    linkProps: { to: '/students' },
    shouldRender: isAirAdmin,
  },
  {
    label: 'Lessons',
    iconComponent: Library,
    linkProps: { to: '/lessons', search: { pageSize: DEFAULT_PAGE_SIZE, pageIndex: 0 } },
    shouldRender: isAirAdmin,
  },
  {
    label: 'Accounts',
    iconComponent: CircleUser,
    linkProps: { to: '/users' },
    shouldRender: isAirAdmin,
  },
];

function getMenuNavLinks(actingUser: LoggedInUser | undefined, pm2Enabled: boolean): NavLinkDefinition[] {
  const navLinks = getNavLinks(pm2Enabled);

  if (!actingUser) return navLinks;

  if (isStudent(actingUser)) {
    const grade = (actingUser.grade ?? 'null') as StudentYearSelectable;
    const maxBookLevel = maxBookPartForStudentGrade(grade);
    const maxBookLevelArray = Array.from({ length: maxBookLevel }, (_, i) => maxBookLevel - i);
    const booksNav: NavLinkDefinition = {
      label: 'Books',
      iconComponent: BookOpen,
      linkProps: { to: '/books' },
      shouldRender: isStudent,
      children: maxBookLevelArray.map((partId) => ({
        label: `Part ${partId}`,
        iconComponent: Book,
        linkProps: {
          to: '/books/part/$partId',
          params: { partId: String(partId) },
        },
        shouldRender: isStudent,
      })),
    };

    const handbookIndex = navLinks.findIndex((nav) => 'label' in nav && nav.label === 'My classes');
    return handbookIndex === -1
      ? [...navLinks, booksNav]
      : [...navLinks.slice(0, handbookIndex), booksNav, ...navLinks.slice(handbookIndex)];
  }

  return navLinks;
}

function RootLayoutComponent() {
  const { loggedInUser, actingUser, loading: isLoadingAuthState } = useContext(AuthContext);
  const pm2Enabled = useFeatureFlag('payments-migration-v2', false);
  const matchRoute = useMatchRoute();
  const logError = useErrorLogger();
  const menuNavLinks = getMenuNavLinks(actingUser, pm2Enabled);
  const isAdmin = actingUser ? isAirAdmin(actingUser) : false;
  const userIsStudent = actingUser ? isStudent(actingUser) : false;

  const matchedNavLinks: NavLinkDefinition[] = menuNavLinks.map((navLink) => {
    if ('linkProps' in navLink) {
      return {
        ...navLink,
        fuzzyMatched: !!matchRoute({ to: navLink.linkProps.to, params: navLink.linkProps.params, fuzzy: true }),
        exactMatched: !!matchRoute({ to: navLink.linkProps.to, params: navLink.linkProps.params }),
        children: (navLink.children || EmptyArray).map((child) => ({
          ...child,
          fuzzyMatched: !!matchRoute({ to: child.linkProps.to, params: child.linkProps.params, fuzzy: true }),
          exactMatched: !!matchRoute({ to: child.linkProps.to, params: child.linkProps.params }),
        })),
      };
    }
    return navLink;
  });

  if (isLoadingAuthState) {
    return <LoadingPage />;
  }

  if (!loggedInUser) {
    const redirect = window.location.pathname;

    if (redirect !== '/') {
      return <Navigate to="/login" search={{ redirect }} replace />;
    }

    return <Navigate to="/login" />;
  }

  return (
    <>
      <main>
        <div className="flex-1 items-start md:grid md:grid-cols-[220px_minmax(0,1fr)] lg:grid-cols-[240px_minmax(0,1fr)] px-0">
          <aside
            className={cn(
              'flex justify-between items-center flex-col h-screen max-h-screen w-[15rem] border-r border-border',
              { 'bg-[#eb404a]': userIsStudent },
            )}
          >
            <div className="container px-0 py-2 grid overflow-auto">
              <div className="container p-2 flex items-center justify-start mb-4 gap-1">
                <Link to="/" className="flex items-center justify-center" data-testid={NAV.logo}>
                  {userIsStudent ? (
                    <img src="/white-logo.svg" alt="Air Reading Logo" className="h-10" />
                  ) : (
                    <img src="/logo.svg" alt="Air Reading Logo" className="h-10" />
                  )}
                </Link>
                {isAdmin && (
                  <div className="text-xs text-gray-400 h-[75%] flex items-center border-l border-border pl-2">
                    Admin portal
                  </div>
                )}
              </div>
              {matchedNavLinks.map((definition, index) => {
                if ('isDivider' in definition) {
                  const visibleItem = actingUser
                    ? shouldRenderItem(
                        {
                          ...actingUser,
                          fullName: actingUser.fullNameOrEmail,
                        },
                        definition.shouldRender,
                      )
                    : true;
                  if (!visibleItem) return null;

                  return <hr key={`divider-${index}`} className="my-2 border-t border-border" />;
                }

                if ('externalURL' in definition) {
                  return <ExternalNavLink key={definition.label} linkDefinition={definition} />;
                }

                return <NavLink key={`${definition.label}-${definition.linkProps.to}`} linkDefinition={definition} />;
              })}
            </div>
            <div className="container p-2 flex items-center gap-x-2 justify-between border-t border-border">
              <UserAvatarMenu />
              {isAirAdmin(loggedInUser) && (
                <ImpersonationSwitcher
                  trigger={
                    <Button
                      icon={PersonStanding}
                      variant={'ghost'}
                      className={cn('h-[4rem] w-[4rem] rounded-full', {
                        '[&>*]:stroke-white hover:bg-[#d23841]': userIsStudent,
                      })}
                    />
                  }
                />
              )}
            </div>
          </aside>
          <GeneralErrorBoundary
            onError={(e) => {
              // Log error to Sentry
              logError(e, {
                component: 'layout',
                action: 'session-load',
                userId: actingUser?.id,
              });

              return {
                allowRetry: true,
                errorHeader: 'Failed to load session. Please try again.',
              };
            }}
          >
            <div className="relative px-6 overflow-auto h-screen bg-[#F7F7F9] flex flex-col">
              <ImpersonationAlertBanner />
              <Outlet />
            </div>
          </GeneralErrorBoundary>
        </div>
      </main>
    </>
  );
}
